import { useLocation } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import ContactForm from "../components/ContactForm";
import useSEO from "../hooks/useSEO";
import { BUSINESS, LOCAL_BUSINESS_SCHEMA } from "../config/business";

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M19.321 6.733a5.78 5.78 0 0 1-3.77-1.39 5.78 5.78 0 0 1-1.97-3.343h-3.297v12.63a2.69 2.69 0 1 1-2.69-2.69c.153 0 .302.013.447.038v-3.356a6.047 6.047 0 1 0 5.54 6.008V9.24a9.027 9.027 0 0 0 5.74 2.04V7.938a5.812 5.812 0 0 1 0-1.205z" />
    </svg>
  );
}

export default function ContactPage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const presetService = params.get("service") || "";

  useSEO({
    title:
      "Contact Woodstone Construction — Decking & Landscaping Quotes Melbourne",
    description:
      "Request a decking, pergola, artificial turf or landscaping quote from Woodstone Construction in Melbourne. Call 0416 164 291 or send us a message.",
    canonical:
      typeof window !== "undefined"
        ? `${window.location.origin}/contact`
        : undefined,
    jsonLd: LOCAL_BUSINESS_SCHEMA,
  });

  const INFO = [
    { icon: MapPin, label: "Location", value: BUSINESS.location },
    {
      icon: Phone,
      label: "Phone",
      value: BUSINESS.phoneDisplay,
      href: `tel:${BUSINESS.phone}`,
    },
    {
      icon: Mail,
      label: "Email",
      value: BUSINESS.email,
      href: `mailto:${BUSINESS.email}`,
    },
    { icon: MapPin, label: "Service area", value: BUSINESS.serviceArea },
  ];

  return (
    <main data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8">
        <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-4">
          Get in touch
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-tight leading-[1.05] font-medium max-w-3xl"
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          Contact Woodstone Construction.
        </h1>
        <p className="mt-5 text-lg text-[#4A4D4A] max-w-2xl leading-relaxed">
          Tell us about your outdoor project — we&#39;ll get back to you with
          pricing, timing, and a plan. For urgent jobs, give us a call
          directly.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Contact details */}
          <aside className="lg:col-span-2">
            <div className="bg-[#0D0D0E] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#7A2D2F] blur-3xl opacity-60"
              />
              <div className="relative">
                <img
                  src={BUSINESS.logoUrl}
                  alt="Woodstone Construction"
                  className="h-14 md:h-16 w-auto object-contain mb-4"
                />
                <p className="text-[#BDBDB4] mb-8">{BUSINESS.tagline}</p>

                <ul className="space-y-5">
                  {INFO.map(({ icon: Icon, label, value, href }) => (
                    <li
                      key={label}
                      className="flex items-start gap-3"
                      data-testid={`contact-info-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-[#C9A876]" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-[#BDBDB4]">
                          {label}
                        </div>
                        {href ? (
                          <a
                            href={href}
                            className="text-white hover:text-[#F3E8E5] text-[15px] break-all"
                          >
                            {value}
                          </a>
                        ) : (
                          <div className="text-white text-[15px]">{value}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="h-px bg-white/15 my-8" />

                <div className="text-[11px] uppercase tracking-[0.2em] text-[#BDBDB4] mb-3">
                  Follow our builds
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={BUSINESS.socials.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-all rounded-full px-4 py-2 text-sm"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={15} /> Instagram
                  </a>
                  <a
                    href={BUSINESS.socials.tiktok}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-all rounded-full px-4 py-2 text-sm"
                    data-testid="contact-tiktok"
                  >
                    <TikTokIcon width={14} height={14} /> TikTok
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <ContactForm defaultService={presetService} />
          </div>
        </div>
      </section>
    </main>
  );
}
